import type { InvoiceStatus, InvoiceType, InvoiceLanguage } from '../../../services/apiService';
import type { StatusTone } from '../ui/StatusPill';
import { formatCurrency, formatNumber } from '../../../i18n/locale';

// ---- Status → tone + label-key mapping ----
//
// This module is not a React component, so it cannot call the `t(...)` hook.
// Instead it exposes i18next label keys; the consuming components resolve them
// with their own `t(...)`.

interface StatusMeta {
  tone: StatusTone;
  labelKey: string;
}

const STATUS_META: Record<InvoiceStatus, StatusMeta> = {
  draft:    { tone: 'neutral', labelKey: 'billing.status.draft' },
  sent:     { tone: 'info',    labelKey: 'billing.status.sent' },
  accepted: { tone: 'accent',  labelKey: 'billing.status.accepted' },
  rejected: { tone: 'danger',  labelKey: 'billing.status.rejected' },
  issued:   { tone: 'info',    labelKey: 'billing.status.issued' },
  paid:     { tone: 'success', labelKey: 'billing.status.paid' },
  canceled: { tone: 'danger',  labelKey: 'billing.status.canceled' },
};

export const statusTone = (s: InvoiceStatus): StatusTone => STATUS_META[s]?.tone || 'neutral';
/** Returns an i18next key (resolve with `t(...)`); falls back to the raw status. */
export const statusLabelKey = (s: InvoiceStatus): string => STATUS_META[s]?.labelKey || s;

// ---- Type label keys ----

const TYPE_LABEL_KEYS: Record<InvoiceType, string> = {
  offer: 'billing.type.offer',
  invoice: 'billing.type.invoice',
  cancellation: 'billing.type.cancellation',
};
/** Returns an i18next key (resolve with `t(...)`); falls back to the raw type. */
export const typeLabelKey = (t: InvoiceType): string => TYPE_LABEL_KEYS[t] || t;

// ---- Money / date formatters (locale-aware via i18n) ----

/** Plain number with 2 decimals in the active locale (no currency symbol). */
const fmtPlain = (n: number): string =>
  formatNumber(n, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Amount in cents → "1.450,00 EUR" style with currency suffix (locale-aware). */
export const fmtMoney = (cents: number, currency: string = 'EUR'): string => {
  const sign = cents < 0 ? '-' : '';
  const n = Math.abs(Number(cents) || 0) / 100;
  return `${sign}${fmtPlain(n)} ${currency}`;
};

/** Amount in cents → plain number string (no currency), locale-aware. */
export const fmtMoneyPlain = (cents: number): string => {
  const sign = cents < 0 ? '-' : '';
  const n = Math.abs(Number(cents) || 0) / 100;
  return `${sign}${fmtPlain(n)}`;
};

/** Amount in cents → fully localized currency string (symbol + locale). */
export const fmtCurrency = (cents: number, currency: string = 'EUR'): string =>
  formatCurrency((Number(cents) || 0) / 100, currency);

export const parseMoneyInput = (text: string): number => {
  // Accept "1.450,00" / "1450" / "1450.00" / "1,450.00"
  if (!text) return 0;
  const cleaned = text.trim().replace(/\s+/g, '');
  // If both . and , present, the rightmost is the decimal separator.
  const lastDot = cleaned.lastIndexOf('.');
  const lastComma = cleaned.lastIndexOf(',');
  let normalized: string;
  if (lastDot === -1 && lastComma === -1) {
    normalized = cleaned;
  } else if (lastDot > lastComma) {
    // dot is decimal — strip commas
    normalized = cleaned.replace(/,/g, '');
  } else {
    // comma is decimal — strip dots, replace comma with dot
    normalized = cleaned.replace(/\./g, '').replace(',', '.');
  }
  const n = Number(normalized);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
};

export const fmtDateDe = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
};

export const todayIso = (): string => new Date().toISOString().slice(0, 10);

// ---- Language meta (label keys) ----

export const LANG_LABEL_KEY: Record<InvoiceLanguage, string> = {
  de: 'billing.lang.de',
  es: 'billing.lang.es',
  en: 'billing.lang.en',
};
